import { Directive, Input } from '@angular/core'
import {
  AddTemplateComplete,
  selectTemplateById,
  selectTemplatesForAutomationEvent,
  Template,
  TemplateService,
  TemplateType,
} from '@tokeet-frontend/templates'
import { select, Store } from '@ngrx/store'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { ActionFailed, ConfirmDialogService, SaveForm, stripTags, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { filter, map, switchMap, tap } from 'rxjs/operators'
import * as lodash from 'lodash'
import { combineLatest, of } from 'rxjs'
import { UntypedFormGroup } from '@angular/forms'
import { AutopilotAutomationBaseComponent } from './autopilot-automation.component'
import { AutomationHandler, AutomationTypes, eventHandlerToMessageTemplateType } from '@tokeet-frontend/automations'
import { Actions, ofType } from '@ngrx/effects'
import { AutomationWizardCloseAction, AutomationWizardSaveAction } from '../automations-wizard.actions'

@Directive()
export abstract class NotifyAutomationBaseComponent extends AutopilotAutomationBaseComponent {
  @Input() type = AutomationTypes.Message
  @Input() handler = AutomationHandler.SendEmailMessage

  @Input() delayed = false
  @Input() messageSubject: string
  @Input() messageBody: string

  template: Template

  protected constructor(
    protected actions: Actions,
    protected store: Store<any>,
    protected amplitudeService: AmplitudeService,
    protected templateService: TemplateService,
    protected confirm: ConfirmDialogService
  ) {
    super(store, amplitudeService, confirm)
  }

  ngOnInit(): void {
    super.ngOnInit()

    combineLatest([
      this.automation$.pipe(switchMap((item) => this.store.pipe(select(selectTemplateById(item?.template))))),
      this.store.pipe(
        select(selectTemplatesForAutomationEvent(this.event)),
        map((items) =>
          items.filter((t) => t.type === TemplateType.Email && t.attributes?.autopilot === this.getAutopilotId())
        ),
        map((items) => lodash.head(items))
      ),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([automationTemplate, template]) => {
        automationTemplate = automationTemplate || template
        this.template = automationTemplate
      })

    this.actions
      .pipe(
        ofType(AutomationWizardSaveAction),
        filter(() => this.viewType !== 'table'),
        untilDestroy(this)
      )
      .subscribe((action) => {
        if (this.automationGroup) {
          this.onSave(this.form, action.closeAfterSave)
        } else if (action.closeAfterSave) {
          this.store.dispatch(AutomationWizardCloseAction())
        }
      })
  }

  normalizeTemplateContent(content: string, type: TemplateType) {
    content = lodash.trim(content)
    if (type !== TemplateType.Email) {
      // remove html tags
      return stripTags(content).trim()
    } else {
      return lodash.replace(content, /\n/g, '<br>')
    }
  }

  tryToCreateTemplate() {
    if (this.template) return of(this.template)
    const templateType = eventHandlerToMessageTemplateType(this.handler)

    // add Signature
    const messageBody = `${this.messageBody}

      Best regards,
      AdvanceCM
    `

    return this.templateService
      .add(
        {
          name: this.name || this.label,
          description: '',
          subject: this.messageSubject,
          body: this.normalizeTemplateContent(messageBody, templateType),
          type: templateType,
          event: this.event,
          attributes: {
            autopilot: this.getAutopilotId(),
          },
        },
        { silent: true }
      )
      .pipe(tap((t) => this.store.dispatch(AddTemplateComplete({ template: t }))))
  }

  tryToCreateOrUpdateAutomation(status: boolean) {
    if (this.automationGroup) {
      this.toggleAutomation(status)
    } else {
      this.tryToCreateTemplate().subscribe(
        (template) => {
          this.template = template
          this.onSave(this.form)
        },
        (error) => this.store.dispatch(ActionFailed({ error }))
      )
    }
  }

  @SaveForm()
  onSave(form: UntypedFormGroup, closeAfterSave = false) {
    const formData = form.getRawValue()
    this.saveAutomation({ ...formData, template: this.template.id })

    if (closeAfterSave) {
      this.store.dispatch(AutomationWizardCloseAction())
    }
  }
}
